import { format } from "date-fns";

const parseToDate = (date: string | Date): Date | null => {
  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (!date) {
    return null;
  }

  const cleanDateStr = date.split("T")[0];
  const [year, month, day] = cleanDateStr.split("-").map(Number);

  if (year && month && day) {
    const localDate = new Date(year, month - 1, day, 12, 0, 0, 0);

    if (!Number.isNaN(localDate.getTime())) {
      return localDate;
    }
  }

  const parsedDate = new Date(date);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export function parseDateString(
  dateStr: string | Date | null | undefined,
  fallback = "",
): string {
  const parsedDate = dateStr == null ? null : parseToDate(dateStr);

  if (!parsedDate) {
    return fallback;
  }

  return format(parsedDate, "d.M.yyyy");
}

export function formatDanishDateTime(
  date: string | Date | null | undefined,
  fallback = "-",
): string {
  if (date == null || date === "") {
    return fallback;
  }

  const parsedDate = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return fallback;
  }

  return new Intl.DateTimeFormat("da-DK", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsedDate);
}

const toDateOnly = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

// Mirrors the backend's ErNuvaerendePeriode: fromDate <= today < toDate,
// compared on date granularity. toDate is exclusive.
export function isCurrentPeriod(
  fromDate: string | Date | null | undefined,
  toDate: string | Date | null | undefined,
): boolean {
  const from = fromDate == null ? null : parseToDate(fromDate);
  const to = toDate == null ? null : parseToDate(toDate);

  if (!from || !to) {
    return false;
  }

  const today = toDateOnly(new Date());

  return toDateOnly(from) <= today && today < toDateOnly(to);
}

// Perioden er oprettet, men begynder først senere: today < fromDate, på datoniveau.
// Samme sammenligning som isCurrentPeriod, så en periode aldrig er både kommende og
// nuværende. `now` kan gives med, så tests ikke afhænger af dags dato.
export function isUpcomingPeriod(
  fromDate: string | Date | null | undefined,
  now: Date = new Date(),
): boolean {
  const from = fromDate == null ? null : parseToDate(fromDate);

  if (!from) {
    return false;
  }

  return toDateOnly(now) < toDateOnly(from);
}

export function formatDanishLongDate(
  date: string | Date | null | undefined,
  fallback = "Ingen frist",
): string {
  if (!date) {
    return fallback;
  }

  return new Intl.DateTimeFormat("da-DK", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(date));
}
